import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ColorPicker.css';

const ColorPicker = ({ 
  isOpen, 
  onClose, 
  initialColor = '#007bff', 
  onChange,
  anchorElement 
}) => {
  const [color, setColor] = useState(initialColor);
  const [position, setPosition] = useState(null); // Start with null instead of 0,0
  const pickerRef = useRef(null);
  const portalContainerRef = useRef(null);

  // Update internal color when initialColor changes
  useEffect(() => {
    setColor(initialColor);
  }, [initialColor]);

  // Create portal container on mount
  useEffect(() => {
    if (!portalContainerRef.current) {
      portalContainerRef.current = document.createElement('div');
      portalContainerRef.current.id = 'color-picker-portal';
      document.body.appendChild(portalContainerRef.current);
    }

    return () => {
      // Cleanup portal container on unmount
      if (portalContainerRef.current) {
        try {
          // Double-check if container is still in the DOM before removing
          if (document.body.contains(portalContainerRef.current)) {
            document.body.removeChild(portalContainerRef.current);
          }
        } catch (e) {
          console.warn('ColorPicker: Failed to remove portal container:', e);
          // Try alternative cleanup
          try {
            const existingContainer = document.getElementById('color-picker-portal');
            if (existingContainer && existingContainer.parentNode) {
              existingContainer.parentNode.removeChild(existingContainer);
            }
          } catch (e2) {
            console.warn('ColorPicker: Alternative cleanup also failed:', e2);
          }
        } finally {
          portalContainerRef.current = null;
        }
      }
    };
  }, []);

  // Predefined colors palette
  const presetColors = [
    '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8',
    '#6f42c1', '#e83e8c', '#fd7e14', '#20c997', '#6c757d',
    '#ffffff', '#f8f9fa', '#e9ecef', '#dee2e6', '#ced4da',
    '#adb5bd', '#868e96', '#495057', '#343a40', '#212529'
  ];

  // Calculate position relative to anchor element
  useEffect(() => {
    if (isOpen && anchorElement && anchorElement.isConnected) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        try {
          const rect = anchorElement.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // Use fixed positioning - no need for scroll offset
          let top = rect.bottom + 5;
          let left = rect.left;
          
          // Ensure it doesn't go off-screen horizontally
          if (left + 280 > viewportWidth) {
            left = viewportWidth - 290; // 280 + 10 margin
          }
          if (left < 10) {
            left = 10;
          }
          
          // Ensure it doesn't go off-screen vertically
          if (top + 200 > viewportHeight) {
            top = rect.top - 205; // Show above the element
          }
          
          console.log('ColorPicker position:', { top, left, rect });
          
          setPosition({ top, left });
        } catch (e) {
          console.warn('ColorPicker: Failed to calculate position:', e);
          // Fallback to center position
          setPosition({ 
            top: window.innerHeight / 2 - 100, 
            left: window.innerWidth / 2 - 140 
          });
        }
      });
    } else if (!isOpen) {
      // Reset position when closed
      setPosition(null);
    }
  }, [isOpen, anchorElement]);

  // Handle clicks outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if picker ref is still valid and mounted
      if (!pickerRef.current) return;
      
      try {
        if (pickerRef.current && !pickerRef.current.contains(event.target) && 
            anchorElement && anchorElement.isConnected && !anchorElement.contains(event.target)) {
          onClose();
        }
      } catch (e) {
        // Handle any DOM-related errors gracefully
        console.warn('ColorPicker: Error in click outside handler:', e);
        onClose();
      }
    };

    if (isOpen) {
      // Add a small delay to prevent immediate closing
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        try {
          document.removeEventListener('mousedown', handleClickOutside);
        } catch (e) {
          console.warn('ColorPicker: Error removing event listener:', e);
        }
      };
    }
  }, [isOpen, onClose, anchorElement]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleColorChange = (newColor) => {
    setColor(newColor);
    onChange(newColor);
  };

  const handlePresetClick = (presetColor) => {
    handleColorChange(presetColor);
  };

  // Don't render if not open, portal container not ready, or position not calculated yet
  if (!isOpen || !portalContainerRef.current || !position) return null;

  console.log('ColorPicker rendering:', { isOpen, position, anchorElement });

  const colorPickerContent = (
    <div 
      ref={pickerRef}
      className="color-picker-popup"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
    >
      <div className="color-picker-content">
        <div className="color-picker-header">
          <span>Choose Color</span>
          <button 
            className="color-picker-close"
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>
        
        <div className="color-picker-body">
          {/* HTML5 Color Input */}
          <div className="color-input-section">
            <input
              type="color"
              value={color}
              onChange={(e) => handleColorChange(e.target.value)}
              className="color-input-native"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => {
                if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                  handleColorChange(e.target.value);
                }
              }}
              className="color-input-text"
              placeholder="#007bff"
              maxLength={7}
            />
          </div>

          {/* Preset Colors */}
          <div className="color-presets">
            <div className="color-presets-label">Quick Colors:</div>
            <div className="color-presets-grid">
              {presetColors.map((presetColor) => (
                <button
                  key={presetColor}
                  className={`color-preset ${color === presetColor ? 'active' : ''}`}
                  style={{ backgroundColor: presetColor }}
                  onClick={() => handlePresetClick(presetColor)}
                  title={presetColor}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Use createPortal to render the color picker in a separate DOM container
  return createPortal(colorPickerContent, portalContainerRef.current);
};

export default ColorPicker; 